
import { PaginatedResponse } from "@/services/api/associationApi";
import { config } from "@/config";

/**
 * Extract data array from either a direct array response or a paginated response
 * This helps maintain backward compatibility with both types of responses
 */
export function extractDataArray<T>(response: T[] | PaginatedResponse<T> | null | undefined): T[] {
  if (!response) {
    return [];
  }
  
  if (Array.isArray(response)) {
    return response;
  }
  
  if ('data' in response && Array.isArray(response.data)) {
    return response.data;
  }
  
  return [];
}

import { supabase } from "@/integrations/supabase/client";

// Get the URL and anon key from the supabase client
export const SUPABASE_URL = `${config.SUPABASE_URL}/functions/v1`;
export const SUPABASE_ANON_KEY = config.SUPABASE_ANON_KEY;

/**
 * Generic API request function with error handling.
 */
export async function apiRequest<T>(
  url: string,
  options?: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS';
    body?: any;
    headers?: Record<string, string>;
    responseType?: 'json' | 'text' | 'blob';
  }
): Promise<T> {
  console.log(`[apiUtils] Making ${options?.method || 'GET'} request to ${url}`);
  
  // Get the session to check for an auth token
  const { data: { session } } = await supabase.auth.getSession();
  const authToken = session.access_token;
  
  const headers: HeadersInit = {
    ...(options?.headers || {})
  };

  // Only add Content-Type header for non-FormData requests
  if (!(options?.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  headers['apikey'] = SUPABASE_ANON_KEY;
  
  // Use the session token if available, otherwise use the anon key
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
    console.log('[apiUtils] Using auth token for request');
  } else {
    headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
    console.log('[apiUtils] Using anon key for request');
  }

  const requestOptions: RequestInit = {
    method: options?.method || 'GET',
    headers,
    mode: 'cors', // Explicitly set CORS mode
  };

  // Add body data for non-GET requests
  if (options?.method !== 'GET' && options?.method !== 'OPTIONS' && options?.body) {
    if (options.body instanceof FormData) {
      // Do not stringify FormData - pass it directly
      requestOptions.body = options.body;
      console.log('[apiUtils] Sending form data:', 
        [...options.body.entries()].map(entry => 
          entry[0] === 'file' ? 
            `${entry[0]}: [File: ${(entry[1] as File).name}]` : 
            `${entry[0]}: ${entry[1]}`
        )
      );
    } else {
      requestOptions.body = JSON.stringify(options.body);
      console.log('[apiUtils] Request body:', JSON.stringify(options.body, null, 2));
    }
  }

  try {
    console.log('[apiUtils] Sending fetch request with options:', { 
      method: requestOptions.method, 
      url, 
      headers: Object.keys(headers),
      body: requestOptions.body ? 
        (requestOptions.body instanceof FormData ? 
          'FormData Present' : 'JSON Present') 
        : 'Not present'
    });
    
    const response = await fetch(url, requestOptions);
    console.log(`[apiUtils] Received response with status: ${response.status}`);
    
    // For OPTIONS requests or DELETE requests with no content
    if (options?.method === 'OPTIONS' || (options?.method === 'DELETE' && response.status === 204)) {
      return {} as T;
    }

    // Check if we should parse as json, text or blob based on options or content-type header
    const responseType = options?.responseType || getResponseTypeFromHeader(response.headers);
    
    // Handle different response types
    let responseData;
    try {
      if (responseType === 'text') {
        responseData = await response.text();
        console.log('[apiUtils] Parsed text response');
      } else if (responseType === 'blob') {
        responseData = await response.blob();
        console.log('[apiUtils] Created blob from response');
      } else {
        // Default to JSON
        responseData = await response.json();
        console.log('[apiUtils] Parsed JSON response');
      }
    } catch (e) {
      console.error('[apiUtils] Failed to parse response:', e);
      throw new Error('Failed to parse server response');
    }

    // Handle API error responses
    if (!response.ok) {
      console.error('[apiUtils] Request failed:', responseData);
      const errorMessage = 
        typeof responseData === 'object' && responseData !== null ? 
          responseData.message || responseData.error || 'Something went wrong' :
          'Something went wrong';
      
      // Create an error object with status code
      const error = new Error(errorMessage) as Error & { status: number; response?: any };
      error.status = response.status;
      error.response = responseData;
      throw error;
    }

    return responseData as T;
  } catch (error) {
    console.error('[apiUtils] Request error:', error);
    throw error;
  }
}

/**
 * Helper function to determine the response type based on Content-Type header
 */
function getResponseTypeFromHeader(headers: Headers): 'json' | 'text' | 'blob' {
  const contentType = headers.get('Content-Type') || '';
  
  if (contentType.includes('application/json')) {
    return 'json';
  }
  
  if (contentType.includes('text/csv') || 
      contentType.includes('text/plain') ||
      contentType.includes('text/html')) {
    return 'text';
  }

  if (contentType.includes('application/octet-stream') || 
      contentType.includes('image/') ||
      contentType.includes('application/pdf')) {
    return 'blob';
  }
  
  // Default to JSON if we can't determine type
  return 'json';
}
